import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { customerUnitPrice } from '../utils/planningConstants';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Search, MapPin, Star, DollarSign, ChefHat, Wine, Music, Users, Truck, Camera,
  Sparkles, CheckCircle2, Package, Clock, ListChecks, Building2, ShoppingCart, Plus, X, FileText, Zap, Send, Mail, Copy, Scale, Tag
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_CATEGORIES = [
  { id: 'all', name: 'All', emoji: null, icon: <Sparkles className="h-5 w-5" />, vendorCategories: [] },
  { id: 'food', name: 'Food & Catering', emoji: null, icon: <ChefHat className="h-5 w-5" />, vendorCategories: ['chef'] },
  { id: 'drinks', name: 'Bar & Beverages', emoji: null, icon: <Wine className="h-5 w-5" />, vendorCategories: ['bartender'] },
  { id: 'entertainment', name: 'Entertainment', emoji: null, icon: <Music className="h-5 w-5" />, vendorCategories: ['dj'] },
  { id: 'staffing', name: 'Staffing', emoji: null, icon: <Users className="h-5 w-5" />, vendorCategories: ['server', 'coordinator'] },
  { id: 'photography', name: 'Photography', emoji: null, icon: <Camera className="h-5 w-5" />, vendorCategories: ['photographer'] },
  { id: 'decor', name: 'Decor', emoji: null, icon: <Sparkles className="h-5 w-5" />, vendorCategories: [] },
  { id: 'logistics', name: 'Logistics', emoji: null, icon: <Truck className="h-5 w-5" />, vendorCategories: ['setup'] }
];

const CATEGORY_ICONS = {
  food: <ChefHat className="h-4 w-4" />,
  drinks: <Wine className="h-4 w-4" />,
  entertainment: <Music className="h-4 w-4" />,
  staffing: <Users className="h-4 w-4" />,
  photography: <Camera className="h-4 w-4" />,
  decor: <Sparkles className="h-4 w-4" />,
  logistics: <Truck className="h-4 w-4" />
};

const PLACEHOLDER_IMAGES = {
  chef: 'https://images.unsplash.com/photo-1577219491135-ce391730fb2c?w=400',
  bartender: 'https://images.unsplash.com/photo-1516997121675-4c2d1684aa3e?w=400',
  dj: 'https://images.unsplash.com/photo-1571266028243-3716f02d2d2e?w=400',
  photographer: 'https://images.unsplash.com/photo-1554048612-b6a482bc67e5?w=400',
  server: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=400',
  coordinator: 'https://images.unsplash.com/photo-1560439514-4e9645039924?w=400',
  setup: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=400'
};

const vendorToServiceCategory = (vendorCat) => {
  const map = { chef: 'food', bartender: 'drinks', dj: 'entertainment', photographer: 'entertainment', server: 'staffing', coordinator: 'staffing', setup: 'logistics' };
  return map[vendorCat] || 'other';
};

export default function Marketplace() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [viewMode, setViewMode] = useState('services'); // 'services' or 'vendors'
  const [expandedService, setExpandedService] = useState(null);
  const [proposalCart, setProposalCart] = useState([]);
  const [serviceRatings, setServiceRatings] = useState({});

  const { user, token } = useAuth();
  const isAdmin = user?.role === 'admin';

  const addToProposalCart = (svc) => {
    if (proposalCart.find(s => s.item_id === svc.item_id)) {
      toast.info(`${svc.name} already in cart`);
      return;
    }
    setProposalCart(prev => [...prev, svc]);
    toast.success(`${svc.name} added to proposal`);
  };

  const removeFromCart = (itemId) => {
    setProposalCart(prev => prev.filter(s => s.item_id !== itemId));
  };

  const goToProposalWithCart = () => {
    sessionStorage.setItem('marketplace_services', JSON.stringify(proposalCart));
    navigate('/admin/proposals/new');
  };

  const [showQuickQuote, setShowQuickQuote] = useState(false);
  const [quickQuoteForm, setQuickQuoteForm] = useState({
    client_name: '', client_email: '', client_phone: '',
    event_type: '', event_name: '', event_date: '', guest_count: 50, notes: ''
  });
  const [sendingQuote, setSendingQuote] = useState(false);
  const [quickQuoteResult, setQuickQuoteResult] = useState(null);

  const handleQuickQuote = async () => {
    if (!quickQuoteForm.client_name || !quickQuoteForm.client_email) {
      toast.error('Client name and email are required');
      return;
    }
    setSendingQuote(true);
    try {
      const res = await axios.post(`${API}/proposals/quick-quote`, {
        ...quickQuoteForm,
        guest_count: parseInt(quickQuoteForm.guest_count) || 50,
        services: proposalCart.map(svc => ({
          item_id: svc.item_id,
          name: svc.name,
          category: svc.service_category || svc.category,
          description: svc.description,
          price: svc.price,
          quantity: 1,
          image_url: svc.image_url,
          what_to_expect: svc.what_to_expect,
          whats_included: svc.whats_included
        }))
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuickQuoteResult(res.data);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quick quote');
    } finally {
      setSendingQuote(false);
    }
  };

  const resetQuickQuote = () => {
    setShowQuickQuote(false);
    setQuickQuoteResult(null);
    setQuickQuoteForm({ client_name: '', client_email: '', client_phone: '', event_type: '', event_name: '', event_date: '', guest_count: 50, notes: '' });
    setProposalCart([]);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vendorsRes, servicesRes, ratingsRes] = await Promise.all([
        axios.get(`${API}/vendors`),
        axios.get(`${API}/marketplace/services`),
        axios.get(`${API}/reviews/service-ratings`).catch(() => ({ data: {} }))
      ]);
      setVendors(vendorsRes.data);
      setServices(servicesRes.data);
      setServiceRatings(ratingsRes.data || {});
    } catch (error) {
      toast.error('Failed to load marketplace');
    } finally {
      setLoading(false);
    }
  };

  const getProfileImage = (vendor) => {
    if (vendor.profile_image) return `${API}/files/${vendor.profile_image}`;
    return PLACEHOLDER_IMAGES[vendor.category] || PLACEHOLDER_IMAGES.chef;
  };

  // Filter services
  const filteredServices = services.filter(svc => {
    if (activeCategory !== 'all' && svc.service_category !== activeCategory) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!(svc.name?.toLowerCase().includes(q) || svc.description?.toLowerCase().includes(q) || 
            svc.what_to_expect?.toLowerCase().includes(q) || svc.service_category?.toLowerCase().includes(q) ||
            svc.whats_included?.some(i => i.toLowerCase().includes(q)))) return false;
    }
    return true;
  });

  // Filter vendors
  const filteredVendors = vendors.filter(vendor => {
    if (activeCategory !== 'all') {
      const cat = SERVICE_CATEGORIES.find(c => c.id === activeCategory);
      if (cat && cat.vendorCategories.length > 0 && !cat.vendorCategories.includes(vendor.category)) return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!(vendor.business_name?.toLowerCase().includes(q) || vendor.description?.toLowerCase().includes(q) ||
            vendor.skills?.some(s => s.toLowerCase().includes(q)) || vendor.category?.toLowerCase().includes(q))) return false;
    }
    if (locationFilter) {
      const loc = locationFilter.toLowerCase();
      if (!vendor.service_areas?.some(a => a.toLowerCase().includes(loc))) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-foreground mb-4" data-testid="supplier-hub-title">
            Supplier Hub
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Browse services and connect with vetted suppliers for your events
          </p>
        </div>

        {/* Search & Location */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8 max-w-3xl mx-auto">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search services, vendors, skills..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-card border-border text-foreground h-12"
              data-testid="marketplace-search"
            />
          </div>
          <div className="relative sm:w-60">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Location..."
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="pl-10 bg-card border-border text-foreground h-12"
              data-testid="marketplace-location"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {SERVICE_CATEGORIES.map(cat => (
            <Button
              key={cat.id}
              variant={activeCategory === cat.id ? 'default' : 'outline'}
              onClick={() => setActiveCategory(cat.id)}
              className={`rounded-full px-5 py-2 text-sm ${
                activeCategory === cat.id
                  ? 'bg-indigo-500 hover:bg-indigo-600 text-foreground'
                  : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
              data-testid={`cat-${cat.id}`}
            >
              {cat.emoji ? <span className="mr-2 text-lg">{cat.emoji}</span> : cat.icon && <span className="mr-2">{cat.icon}</span>}
              {cat.name}
            </Button>
          ))}
        </div>

        {/* View Toggle */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-muted-foreground text-sm">
            {viewMode === 'services' ? `${filteredServices.length} services` : `${filteredVendors.length} suppliers`} found
            {activeCategory !== 'all' && ` in ${SERVICE_CATEGORIES.find(c => c.id === activeCategory)?.name}`}
          </p>
          <div className="flex bg-card rounded-lg p-1 border border-border">
            <button
              onClick={() => setViewMode('services')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'services' ? 'bg-indigo-500 text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="view-services"
            >
              <Package className="h-4 w-4 inline mr-1.5" />Services
            </button>
            <button
              onClick={() => setViewMode('vendors')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'vendors' ? 'bg-indigo-500 text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="view-suppliers"
            >
              <Building2 className="h-4 w-4 inline mr-1.5" />Suppliers
            </button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <div key={i} className="bg-card rounded-xl h-72 animate-pulse border border-border" />)}
          </div>
        ) : viewMode === 'services' ? (
          /* ============ SERVICES VIEW ============ */
          filteredServices.length === 0 ? (
            <div className="text-center py-16">
              <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No services found</h3>
              <p className="text-muted-foreground">Try adjusting your search or category</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredServices.map(svc => (
                <Card 
                  key={svc.item_id}
                  className="bg-card border-border overflow-hidden hover:border-indigo-500/40 transition-all group"
                  data-testid={`service-card-${svc.item_id}`}
                >
                  {/* Service Image */}
                  {svc.image_url && (
                    <div className="relative h-44 overflow-hidden">
                      <img 
                        src={svc.image_url} 
                        alt={svc.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <Badge className="absolute top-3 right-3 bg-indigo-500/90 text-foreground text-xs capitalize">
                        {CATEGORY_ICONS[svc.service_category]} <span className="ml-1">{svc.service_category}</span>
                      </Badge>
                    </div>
                  )}
                  
                  <CardContent className="p-4 space-y-3">
                    <h3 className="font-heading text-lg font-bold text-foreground flex items-center gap-2">
                      {!svc.image_url && CATEGORY_ICONS[svc.service_category]} {svc.name}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{svc.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-sm font-semibold" data-testid={`price-${svc.item_id}`}>
                          <DollarSign className="h-3 w-3" />{customerUnitPrice(svc).toFixed(2)}
                          {svc.pricing_unit_label && <span className="ml-0.5 text-xs opacity-75">/{svc.pricing_unit_label}</span>}
                        </Badge>
                        {svc.min_quantity && (
                          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                            <Scale className="h-2.5 w-2.5" /> Min: {svc.min_quantity} {svc.pricing_unit_label || 'units'}
                          </span>
                        )}
                        {serviceRatings[svc.item_id] && (
                          <span className="flex items-center gap-1 text-xs text-yellow-500" data-testid={`rating-${svc.item_id}`}>
                            <Star className="h-3 w-3 fill-yellow-500" />
                            {serviceRatings[svc.item_id].avg_rating}
                            <span className="text-muted-foreground">({serviceRatings[svc.item_id].count})</span>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {svc.vendor_count > 0 && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {svc.vendor_count} supplier{svc.vendor_count > 1 ? 's' : ''}
                          </span>
                        )}
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant={proposalCart.find(s => s.item_id === svc.item_id) ? "secondary" : "default"}
                            className={`h-7 text-xs ${proposalCart.find(s => s.item_id === svc.item_id) ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-indigo-500 hover:bg-indigo-600'}`}
                            onClick={(e) => { e.stopPropagation(); addToProposalCart(svc); }}
                            data-testid={`add-to-proposal-${svc.item_id}`}
                          >
                            {proposalCart.find(s => s.item_id === svc.item_id) 
                              ? <><CheckCircle2 className="h-3 w-3 mr-1" />Added</>
                              : <><Plus className="h-3 w-3 mr-1" />Add to Proposal</>
                            }
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Expandable Details */}
                    <button 
                      onClick={() => setExpandedService(expandedService === svc.item_id ? null : svc.item_id)}
                      className="text-indigo-400 text-sm hover:text-indigo-300 transition-colors w-full text-left"
                      data-testid={`expand-${svc.item_id}`}
                    >
                      {expandedService === svc.item_id ? 'Hide Details' : 'View Details'}
                    </button>
                    
                    {expandedService === svc.item_id && (
                      <div className="space-y-3 border-t border-border pt-3">
                        {svc.what_to_expect && (
                          <div>
                            <h4 className="text-xs font-medium text-indigo-400 flex items-center gap-1 mb-1">
                              <Clock className="h-3 w-3" /> What to Expect
                            </h4>
                            <p className="text-xs text-muted-foreground">{svc.what_to_expect}</p>
                          </div>
                        )}
                        {svc.whats_included?.length > 0 && (
                          <div>
                            <h4 className="text-xs font-medium text-indigo-400 flex items-center gap-1 mb-1">
                              <ListChecks className="h-3 w-3" /> What's Included
                            </h4>
                            <ul className="space-y-0.5">
                              {svc.whats_included.map((item, i) => (
                                <li key={i} className="text-xs text-muted-foreground flex items-center gap-1">
                                  <CheckCircle2 className="h-2.5 w-2.5 text-emerald-500 flex-shrink-0" />{item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {/* Vendors offering this service */}
                        {svc.vendors?.length > 0 && (
                          <div>
                            <h4 className="text-xs font-medium text-indigo-400 mb-2">Available From</h4>
                            <div className="space-y-2">
                              {svc.vendors.map(v => (
                                <div 
                                  key={v.vendor_id}
                                  className="flex items-center justify-between p-2 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted"
                                  onClick={() => navigate(`/vendors/${v.vendor_id}`)}
                                >
                                  <div>
                                    <p className="text-xs font-medium text-foreground">{v.business_name}</p>
                                    {v.service_areas?.length > 0 && (
                                      <p className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                                        <MapPin className="h-2.5 w-2.5" />{v.service_areas.slice(0, 2).join(', ')}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {v.rating && (
                                      <span className="text-xs text-yellow-500 flex items-center">
                                        <Star className="h-3 w-3 fill-yellow-500" /> {v.rating.toFixed(1)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        ) : (
          /* ============ VENDORS VIEW ============ */
          filteredVendors.length === 0 ? (
            <div className="text-center py-16">
              <Building2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No suppliers found</h3>
              <p className="text-muted-foreground">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVendors.map(vendor => {
                const serviceCat = vendorToServiceCategory(vendor.category);
                return (
                  <Card 
                    key={vendor.vendor_id}
                    className="bg-card border-border overflow-hidden hover:border-indigo-500/40 transition-all cursor-pointer group"
                    onClick={() => navigate(`/vendors/${vendor.vendor_id}`)}
                    data-testid={`vendor-card-${vendor.vendor_id}`}
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img 
                        src={getProfileImage(vendor)} 
                        alt={vendor.business_name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                      <div className="absolute top-3 right-3 flex gap-2">
                        <Badge className="bg-indigo-500/90 text-foreground text-xs capitalize">{vendor.category}</Badge>
                        {vendor.is_verified && (
                          <Badge className="bg-green-500/90 text-foreground text-xs">
                            <CheckCircle2 className="h-3 w-3 mr-1" />Verified
                          </Badge>
                        )}
                      </div>
                      <div className="absolute bottom-3 left-3 right-3">
                        <h3 className="font-heading text-lg font-bold text-foreground truncate">{vendor.business_name}</h3>
                      </div>
                    </div>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-yellow-500">
                          <Star className="h-4 w-4 fill-yellow-500" />
                          <span className="ml-1 text-sm font-medium text-foreground">{vendor.rating?.toFixed(1) || 'N/A'}</span>
                          <span className="ml-1 text-xs text-muted-foreground">({vendor.reviews_count || 0})</span>
                        </div>
                        <div className="flex items-center text-muted-foreground text-sm">
                          <DollarSign className="h-4 w-4" />{vendor.hourly_rate}/hr
                        </div>
                      </div>
                      <Badge variant="outline" className="border-border text-muted-foreground text-xs">
                        {SERVICE_CATEGORIES.find(c => c.id === serviceCat)?.emoji || ''} {SERVICE_CATEGORIES.find(c => c.id === serviceCat)?.name || vendor.category}
                      </Badge>
                      {vendor.service_areas?.length > 0 && (
                        <div className="flex items-center text-muted-foreground text-xs">
                          <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{vendor.service_areas.slice(0, 3).join(', ')}</span>
                        </div>
                      )}
                      {vendor.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {vendor.skills.slice(0, 4).map((skill, i) => (
                            <Badge key={i} variant="outline" className="border-border text-muted-foreground text-[10px] py-0">{skill}</Badge>
                          ))}
                          {vendor.skills.length > 4 && (
                            <Badge variant="outline" className="border-border text-muted-foreground text-[10px] py-0">+{vendor.skills.length - 4}</Badge>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )
        )}

        {/* Floating Proposal Cart Bar */}
        {isAdmin && proposalCart.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur-sm border-t border-indigo-500/30 p-4 z-50" data-testid="proposal-cart-bar">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-4 overflow-x-auto">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <ShoppingCart className="h-5 w-5 text-indigo-400" />
                  <span className="text-foreground font-medium">{proposalCart.length} service{proposalCart.length > 1 ? 's' : ''}</span>
                </div>
                <div className="flex gap-2 overflow-x-auto">
                  {proposalCart.map(svc => (
                    <Badge key={svc.item_id} className="bg-muted text-muted-foreground border-border gap-1 whitespace-nowrap py-1.5">
                      {svc.name} (${svc.price})
                      <button onClick={() => removeFromCart(svc.item_id)} className="ml-1 hover:text-red-400">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0 ml-4">
                <Button 
                  onClick={() => setShowQuickQuote(true)}
                  className="bg-emerald-500 hover:bg-emerald-600"
                  data-testid="quick-quote-btn"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Quick Quote
                </Button>
                <Button 
                  onClick={goToProposalWithCart}
                  className="bg-indigo-500 hover:bg-indigo-600"
                  data-testid="create-proposal-from-cart"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Full Proposal ({proposalCart.length})
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Quick Quote Dialog */}
        <Dialog open={showQuickQuote} onOpenChange={(open) => { if (!open) setShowQuickQuote(false); }}>
          <DialogContent className="max-w-lg bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-foreground flex items-center gap-2">
                <Zap className="h-5 w-5 text-emerald-400" />
                Quick Quote
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Instantly create and email a quote with {proposalCart.length} service{proposalCart.length > 1 ? 's' : ''}
              </DialogDescription>
            </DialogHeader>

            {quickQuoteResult ? (
              /* Success State */
              <div className="py-6 text-center space-y-4">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Quote Sent!</h3>
                  <p className="text-muted-foreground text-sm mt-1">{quickQuoteResult.proposal_number} &middot; ${quickQuoteResult.total?.toFixed(2)}</p>
                  <p className="text-muted-foreground text-sm">
                    {quickQuoteResult.email_sent ? 'Emailed to client' : 'Saved (email service not configured)'}
                  </p>
                </div>
                <div className="flex flex-col gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    className="border-border text-muted-foreground"
                    onClick={() => {
                      try { navigator.clipboard.writeText(quickQuoteResult.proposal_url); toast.success('Link copied!'); } catch(e) { toast.error('Copy failed'); }
                    }}
                    data-testid="copy-quote-link"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Quote Link
                  </Button>
                  <Button 
                    onClick={() => navigate(`/admin/proposals/${quickQuoteResult.proposal_id}`)}
                    className="bg-indigo-500 hover:bg-indigo-600"
                    data-testid="view-quote-in-pipeline"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    View in Proposals
                  </Button>
                  <Button variant="ghost" className="text-muted-foreground" onClick={resetQuickQuote}>
                    Done
                  </Button>
                </div>
              </div>
            ) : (
              /* Form State */
              <>
                <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
                  {/* Services Summary */}
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-2 font-medium">SERVICES</p>
                    {proposalCart.map(svc => (
                      <div key={svc.item_id} className="flex justify-between text-sm py-1">
                        <span className="text-muted-foreground">{svc.name}</span>
                        <span className="text-emerald-400 font-medium">${customerUnitPrice(svc).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm pt-2 mt-2 border-t border-border">
                      <span className="text-foreground font-medium">Est. Total (incl. fees & tax)</span>
                      <span className="text-emerald-400 font-bold">
                        ${(proposalCart.reduce((sum, s) => sum + customerUnitPrice(s), 0) * 1.2728).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5 col-span-2 sm:col-span-1">
                      <Label className="text-muted-foreground text-xs">Client Name *</Label>
                      <Input value={quickQuoteForm.client_name} onChange={e => setQuickQuoteForm(p => ({...p, client_name: e.target.value}))} placeholder="John Smith" className="bg-muted border-border text-foreground h-9" data-testid="qq-client-name" />
                    </div>
                    <div className="space-y-1.5 col-span-2 sm:col-span-1">
                      <Label className="text-muted-foreground text-xs">Client Email *</Label>
                      <Input type="email" value={quickQuoteForm.client_email} onChange={e => setQuickQuoteForm(p => ({...p, client_email: e.target.value}))} placeholder="john@example.com" className="bg-muted border-border text-foreground h-9" data-testid="qq-client-email" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-muted-foreground text-xs">Phone</Label>
                      <Input value={quickQuoteForm.client_phone} onChange={e => setQuickQuoteForm(p => ({...p, client_phone: e.target.value}))} placeholder="(555) 123-4567" className="bg-muted border-border text-foreground h-9" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-muted-foreground text-xs">Event Type</Label>
                      <Select value={quickQuoteForm.event_type} onValueChange={v => setQuickQuoteForm(p => ({...p, event_type: v}))}>
                        <SelectTrigger className="bg-muted border-border text-foreground h-9" data-testid="qq-event-type">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Wedding">Wedding</SelectItem>
                          <SelectItem value="Corporate">Corporate</SelectItem>
                          <SelectItem value="Birthday">Birthday</SelectItem>
                          <SelectItem value="Social">Social Event</SelectItem>
                          <SelectItem value="Fundraiser">Fundraiser</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-muted-foreground text-xs">Event Name</Label>
                      <Input value={quickQuoteForm.event_name} onChange={e => setQuickQuoteForm(p => ({...p, event_name: e.target.value}))} placeholder="Summer Wedding" className="bg-muted border-border text-foreground h-9" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-muted-foreground text-xs">Event Date</Label>
                      <Input type="date" value={quickQuoteForm.event_date} onChange={e => setQuickQuoteForm(p => ({...p, event_date: e.target.value}))} className="bg-muted border-border text-foreground h-9" data-testid="qq-event-date" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-muted-foreground text-xs">Guest Count</Label>
                      <Input type="number" value={quickQuoteForm.guest_count} onChange={e => setQuickQuoteForm(p => ({...p, guest_count: e.target.value}))} className="bg-muted border-border text-foreground h-9" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-muted-foreground text-xs">Notes</Label>
                      <Input value={quickQuoteForm.notes} onChange={e => setQuickQuoteForm(p => ({...p, notes: e.target.value}))} placeholder="Any special requests..." className="bg-muted border-border text-foreground h-9" />
                    </div>
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowQuickQuote(false)} className="border-border text-muted-foreground">
                    Cancel
                  </Button>
                  <Button onClick={handleQuickQuote} disabled={sendingQuote} className="bg-emerald-500 hover:bg-emerald-600" data-testid="send-quick-quote">
                    <Send className="h-4 w-4 mr-2" />
                    {sendingQuote ? 'Creating...' : 'Create & Send Quote'}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
